import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { useSocketContext } from './SocketContext';
import type { SessionState, UserSettings, ConsumedDrink, TickData, AddDrinkPayload } from '../types';
import { messages } from '../config/messages';

const SESSION_ID_KEY = 'optimalni-promile-session-id';

interface SessionContextType {
  session: SessionState | null;
  isLoading: boolean;
  error: string | null;
  notificationToast: string | null;
  startSession: (settings: UserSettings) => void;
  addDrink: (payload: AddDrinkPayload) => void;
  removeDrink: (drinkId: string) => void;
  resetSession: () => void;
  clearError: () => void;
  clearNotificationToast: () => void;
  requestNotificationPermission: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const { socket, isConnected } = useSocketContext();
  const [session, setSession] = useState<SessionState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notificationToast, setNotificationToast] = useState<string | null>(null);
  const reconnectAttempted = useRef(false);
  const previousBacRef = useRef<number>(0);
  const isInitializedRef = useRef(false);

  const showNotification = useCallback((message: string) => {
    // Service Worker Notification (better mobile support)
    if ('Notification' in window && Notification.permission === 'granted' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        registration.showNotification('Optimální Promile', {
          body: message,
          icon: '/icons/icon-192.png',
          badge: '/icons/icon-192.png',
          tag: 'bac-alert',
          renotify: true
        } as NotificationOptions);
      });
    }
    // In-app toast always
    setNotificationToast(message);
    setTimeout(() => setNotificationToast(null), 5000);
  }, []);

  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }, []);

  const clearNotificationToast = useCallback(() => {
    setNotificationToast(null);
  }, []);

  // Reset reconnect flag when socket disconnects
  useEffect(() => {
    if (!socket) return;

    const handleDisconnect = () => {
      reconnectAttempted.current = false;
    };

    socket.on('disconnect', handleDisconnect);
    return () => {
      socket.off('disconnect', handleDisconnect);
    };
  }, [socket]);

  // Try to reconnect to existing session on mount/reconnect
  useEffect(() => {
    if (!socket || !isConnected || reconnectAttempted.current) return;

    const savedSessionId = localStorage.getItem(SESSION_ID_KEY);
    if (savedSessionId && !session) {
      reconnectAttempted.current = true;
      setIsLoading(true);
      socket.emit('session:reconnect', savedSessionId);
    }
  }, [socket, isConnected, session]);

  // Handle socket events
  useEffect(() => {
    if (!socket) return;

    const handleSessionCreated = (state: SessionState) => {
      localStorage.setItem(SESSION_ID_KEY, state.sessionId);
      // Initialize BAC tracking for new session
      previousBacRef.current = state.currentBac;
      isInitializedRef.current = true;
      setSession(state);
      setIsLoading(false);
      setError(null);
    };

    const handleSessionReconnected = (state: SessionState) => {
      localStorage.setItem(SESSION_ID_KEY, state.sessionId);
      // Initialize BAC tracking - don't notify on reconnect
      previousBacRef.current = state.currentBac;
      isInitializedRef.current = true;
      setSession(state);
      setIsLoading(false);
      setError(null);
    };

    const handleReconnectFailed = () => {
      localStorage.removeItem(SESSION_ID_KEY);
      setIsLoading(false);
    };

    const handleTick = (data: TickData) => {
      setSession(prev => {
        if (!prev) return null;

        const previousBac = previousBacRef.current;
        const newBac = data.currentBac;
        const targetBac = prev.targetBac;

        // Detect threshold crossing: was below, now at or above
        // Only notify if we've been initialized (not on first tick after reconnect)
        if (isInitializedRef.current && previousBac < targetBac && newBac >= targetBac) {
          showNotification(messages.limitExceeded(newBac.toFixed(2)));
        }

        previousBacRef.current = newBac;

        // Update drinks with absorption progress
        const updatedDrinks = prev.drinks.map(drink => {
          const absorption = data.drinks.find(d => d.drinkId === drink.id);
          return absorption
            ? { ...drink, absorptionProgress: absorption.absorptionProgress }
            : drink;
        });

        return {
          ...prev,
          currentBac: data.currentBac,
          trend: data.trend,
          drinks: updatedDrinks
        };
      });
    };

    const handleDrinkAdded = (drink: ConsumedDrink) => {
      setSession(prev => {
        if (!prev) return null;
        return {
          ...prev,
          drinks: [...prev.drinks, drink]
        };
      });
    };

    const handleDrinkRemoved = (drinkId: string) => {
      setSession(prev => {
        if (!prev) return null;
        return {
          ...prev,
          drinks: prev.drinks.filter(d => d.id !== drinkId)
        };
      });
    };

    const handleError = (message: string) => {
      setError(message);
      setIsLoading(false);
    };

    socket.on('session:created', handleSessionCreated);
    socket.on('session:reconnected', handleSessionReconnected);
    socket.on('session:reconnect:failed', handleReconnectFailed);
    socket.on('session:tick', handleTick);
    socket.on('drink:added', handleDrinkAdded);
    socket.on('drink:removed', handleDrinkRemoved);
    socket.on('error', handleError);

    return () => {
      socket.off('session:created', handleSessionCreated);
      socket.off('session:reconnected', handleSessionReconnected);
      socket.off('session:reconnect:failed', handleReconnectFailed);
      socket.off('session:tick', handleTick);
      socket.off('drink:added', handleDrinkAdded);
      socket.off('drink:removed', handleDrinkRemoved);
      socket.off('error', handleError);
    };
  }, [socket, showNotification]);

  const startSession = useCallback((settings: UserSettings) => {
    if (!socket || !isConnected) {
      setError(messages.noConnection);
      return;
    }

    setIsLoading(true);
    setError(null);
    socket.emit('session:start', settings);
  }, [socket, isConnected]);

  const addDrink = useCallback((payload: AddDrinkPayload) => {
    if (!socket || !isConnected) {
      setError(messages.noConnection);
      return;
    }

    socket.emit('drink:add', payload);
  }, [socket, isConnected]);

  const removeDrink = useCallback((drinkId: string) => {
    if (!socket || !isConnected) {
      setError(messages.noConnection);
      return;
    }

    socket.emit('drink:remove', drinkId);
  }, [socket, isConnected]);

  const resetSession = useCallback(() => {
    if (socket && isConnected) {
      socket.emit('session:reset');
    }
    localStorage.removeItem(SESSION_ID_KEY);
    reconnectAttempted.current = false;
    previousBacRef.current = 0;
    isInitializedRef.current = false;
    setSession(null);
    setError(null);
  }, [socket, isConnected]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <SessionContext.Provider
      value={{
        session,
        isLoading,
        error,
        notificationToast,
        startSession,
        addDrink,
        removeDrink,
        resetSession,
        clearError,
        clearNotificationToast,
        requestNotificationPermission
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSessionContext() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSessionContext must be used within a SessionProvider');
  }
  return context;
}
